import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Plus, X, GitMerge, Link2, Download, Trash2, CheckSquare, Flame } from 'lucide-react';
import { toast } from 'sonner';

import { useLeads } from '../hooks/useLeads';
import LeadFilters from '../components/leads/LeadFilters';
import LeadTable from '../components/leads/LeadTable';
import LeadModal from '../components/leads/LeadModal';
import PaymentLinkModal from '../components/leads/PaymentLinkModal';
import QuickPaymentLinkModal from '../components/leads/QuickPaymentLinkModal';
import { LeadRequestsPanel } from '../components/leads/LeadRequestsPanel';
import { RenewalsPanel } from '../components/leads/RenewalsPanel';
import { DeletedLeadsPanel } from '../components/leads/DeletedLeadsPanel';
import { PageHeader } from '../components/ds';
import { getApiUrl } from '../utils/api';
import { gatedDownload } from '../lib/gatedDownload';

const API_URL = getApiUrl();

const LeadsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const {
    leads, loading, total, page, totalPages,
    searchTerm, setSearchTerm,
    leadStatusFilter, setLeadStatusFilter,
    trialPaymentFilter, setTrialPaymentFilter,
    filters, setFilters,
    users, batches, pricingConfigs,
    savedViews, activeView,
    fetchLeads, goToPage,
    resetFilters, applyView,
    fetchSavedViews,
    updateLeadLocal,
    buildFilterParams,
  } = useLeads(searchParams);

  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState('leads'); // leads | support | sales | renewals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQuickPayModal, setShowQuickPayModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [merging, setMerging] = useState(false);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const canEdit = ['super_admin', 'pre_sales', 'sales', 'operations'].includes(user?.role);
  const canMerge = ['super_admin', 'pre_sales'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';

  const exitBulkMode = () => { setBulkMode(false); setBulkSelected([]); setAllMatching(false); };
  const toggleBulkSelect = (id) => {
    setAllMatching(false);
    setBulkSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleBulkSelectPage = (ids, on) => {
    setAllMatching(false);
    setBulkSelected((prev) => (on ? [...new Set([...prev, ...ids])] : prev.filter((x) => !ids.includes(x))));
  };

  const bulkCount = allMatching ? total : bulkSelected.length;

  const handleBulkDelete = async () => {
    if (!bulkCount) return;
    const scope = allMatching
      ? `ALL ${total} leads matching the current filters`
      : `${bulkSelected.length} selected lead${bulkSelected.length === 1 ? '' : 's'}`;
    if (!window.confirm(`Delete ${scope}?\n\nThey move to “Recently deleted” and can be restored for 24 hours, after which they are purged permanently. Payments, bookings and invoices are not deleted.`)) return;
    setBulkDeleting(true);
    try {
      const body = allMatching
        ? { all_matching: true, filters: Object.fromEntries(buildFilterParams()) }
        : { lead_ids: bulkSelected };
      const { data } = await axios.post(`${API_URL}/api/leads/bulk-delete`, body, { withCredentials: true });
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? '' : 's'} — restorable for 24h in “Recently deleted”`);
      exitBulkMode();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openLeadById = async (leadId) => {
    const found = leads.find((l) => l.lead_id === leadId);
    if (found) { setEditingLead(found); setShowModal(true); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/leads/${leadId}`, { withCredentials: true });
      setEditingLead(data); setShowModal(true);
    } catch (e) { toast.error('Lead not found'); }
  };

  // Export the CURRENTLY-FILTERED leads as a CSV (Meta-campaign ready).
  // Reuses the exact same filter params as the on-screen list.
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = buildFilterParams();
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      const saved = await gatedDownload(`/api/leads/export/csv?${params.toString()}`, `leads_export_${ts}.csv`);
      if (saved) toast.success(`Exported ${total} filtered lead${total === 1 ? '' : 's'} to CSV`);
    } catch (error) {
      toast.error('Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  // Count active filters — only count real filter selections, not mode/internal state
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (leadStatusFilter.length > 0) count++;
    if (trialPaymentFilter.length > 0) count++;
    if (filters.enrollment_status.length > 0) count++;
    if (filters.trial_status.length > 0) count++;
    if (filters.consultation_status.length > 0) count++;
    if (filters.country.length > 0) count++;
    if (filters.funnel_type.length > 0) count++;
    if (filters.trial_slot.length > 0) count++;
    if (filters.trial_date_filter !== 'all') count++;
    if (filters.create_date_filter !== 'all') count++;
    if (filters.is_hot === 'true' || filters.is_hot === 'false') count++;
    return count;
  }, [filters, leadStatusFilter, trialPaymentFilter]);

  // Auto-show filters if URL params present
  React.useEffect(() => {
    if (searchParams.get('lead_status') || searchParams.get('trial_status') || searchParams.get('enrollment_status') || searchParams.get('consultation_status') || searchParams.get('trial_payment')) {
      setShowFilters(true);
    }
  }, [searchParams]);

  const handleDelete = async (leadId) => {
    if (!window.confirm('Delete this lead?\n\nIt moves to “Recently deleted” and can be restored for 24 hours.')) return;
    try {
      await axios.delete(`${API_URL}/api/leads/${leadId}`);
      toast.success('Lead deleted — restorable for 24h in “Recently deleted”');
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const saveCurrentView = async () => {
    if (!newViewName.trim()) { toast.error('Please enter a view name'); return; }
    try {
      await axios.post(`${API_URL}/api/analytics/saved-views`, { name: newViewName, filters }, { withCredentials: true });
      toast.success('View saved');
      setShowSaveViewModal(false);
      setNewViewName('');
      fetchSavedViews();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save view'); }
  };

  const deleteView = async (viewId) => {
    try {
      await axios.delete(`${API_URL}/api/analytics/saved-views/${viewId}`, { withCredentials: true });
      toast.success('View deleted');
      fetchSavedViews();
    } catch (error) { toast.error('Failed to delete view'); }
  };

  const toggleMergeSelect = (leadId) => {
    setSelectedForMerge(prev => {
      if (prev.includes(leadId)) return prev.filter(id => id !== leadId);
      if (prev.length >= 2) { toast.error('Select exactly 2 leads to merge'); return prev; }
      return [...prev, leadId];
    });
  };

  const handleMerge = async () => {
    if (selectedForMerge.length !== 2) return;
    setMerging(true);
    // Newer lead (created later) is primary — keeps its data, fills blanks from older
    const lead1 = leads.find(l => l.lead_id === selectedForMerge[0]);
    const lead2 = leads.find(l => l.lead_id === selectedForMerge[1]);
    const newer = new Date(lead1?.created_at) > new Date(lead2?.created_at) ? lead1 : lead2;
    const older = newer === lead1 ? lead2 : lead1;
    try {
      const res = await axios.post(
        `${API_URL}/api/leads/merge?primary_lead_id=${newer.lead_id}&duplicate_lead_id=${older.lead_id}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      setSelectedForMerge([]);
      setShowMergeConfirm(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div data-testid="leads-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Leads</h1>
          <p className="text-muted-foreground">Manage and track your leads</p>
          <div className="mt-3 inline-flex items-center gap-0.5 rounded-lg bg-muted p-1" role="tablist" data-testid="leads-view-tabs">
            {[{ key: 'leads', label: 'All leads' }, { key: 'support', label: 'Support' },
              { key: 'sales', label: 'Sales queries' }, { key: 'renewals', label: 'Renewals' },
              ...(isSuperAdmin ? [{ key: 'deleted', label: 'Recently deleted' }] : [])].map((o) => (
              <button key={o.key} type="button" role="tab" aria-selected={view === o.key} onClick={() => setView(o.key)} data-testid={`leads-view-${o.key}`}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${view === o.key ? 'bg-card text-primary shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}>{o.label}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && view === 'leads' && (
            <button
              onClick={() => (bulkMode ? exitBulkMode() : setBulkMode(true))}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${bulkMode ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              data-testid="bulk-select-toggle"
              title="Tick rows to delete them in bulk"
            >
              <CheckSquare className="w-4 h-4" /> {bulkMode ? 'Exit bulk select' : 'Bulk select'}
            </button>
          )}
          <button
            onClick={handleExportCsv}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="export-leads-csv-btn"
            title="Download the currently-filtered leads as a CSV (Meta-ready)"
          >
            <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          {canEdit && (
            <>
              <button
                onClick={() => setShowQuickPayModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="quick-payment-link-btn"
              >
                <Link2 className="w-4 h-4" /> Payment Link
              </button>
              <button
                onClick={() => { setEditingLead(null); setShowModal(true); }}
                className="btn-primary flex items-center gap-2"
                data-testid="add-lead-btn"
              >
                <Plus className="w-4 h-4" /> Add Lead
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      {(view === 'support' || view === 'sales') && (
        <LeadRequestsPanel kind={view} onOpenLead={openLeadById} />
      )}

      {view === 'deleted' && <DeletedLeadsPanel />}

      {view === 'renewals' && (
        <RenewalsPanel
          onOpenLead={openLeadById}
          onSendRenewalLink={async (leadId) => {
            const found = leads.find((l) => l.lead_id === leadId);
            if (found) { setSelectedLead(found); setShowPaymentModal(true); return; }
            try {
              const { data } = await axios.get(`${API_URL}/api/leads/${leadId}`, { withCredentials: true });
              setSelectedLead(data); setShowPaymentModal(true);
            } catch (e) { toast.error('Lead not found'); }
          }}
        />
      )}

      {view === 'leads' && <LeadFilters
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        showFilters={showFilters} setShowFilters={setShowFilters}
        filters={filters} setFilters={setFilters}
        leadStatusFilter={leadStatusFilter} setLeadStatusFilter={setLeadStatusFilter}
        trialPaymentFilter={trialPaymentFilter} setTrialPaymentFilter={setTrialPaymentFilter}
        batches={batches}
        savedViews={savedViews} activeView={activeView} applyView={applyView} resetFilters={resetFilters}
        onSaveView={() => setShowSaveViewModal(true)}
        onDeleteView={deleteView}
        activeFilterCount={activeFilterCount}
      />}

      {view === 'leads' && (<>
      {/* Payment Quick Toggle */}
      <div className="flex items-center gap-3" data-testid="payment-quick-filter">
        <span className="text-sm font-medium text-slate-600">Payment:</span>
        {[
          { label: 'All', value: [] },
          { label: 'Paid', value: ['paid'] },
          { label: 'Unpaid', value: ['not_paid'] },
        ].map(opt => {
          const isActive = JSON.stringify(trialPaymentFilter) === JSON.stringify(opt.value);
          return (
            <button
              key={opt.label}
              onClick={() => setTrialPaymentFilter(opt.value)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`payment-filter-${opt.label.toLowerCase()}`}
            >
              {opt.label}
            </button>
          );
        })}
        <span className="ml-2 h-5 w-px bg-slate-200" />
        <button
          onClick={() => setFilters({ ...filters, is_hot: filters.is_hot === 'true' ? '' : 'true' })}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            filters.is_hot === 'true'
              ? 'bg-orange-100 text-orange-700 border border-orange-300'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
          title="Lead score above 20, or flagged HOT by the LMS"
          data-testid="hot-leads-quick-filter"
        >
          <Flame className="w-3.5 h-3.5" />
          HOT leads
        </button>
      </div>

      {/* Results count */}
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <span data-testid="leads-count">Showing {leads.length} of {total} leads</span>
        {leadStatusFilter.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
            Status: {leadStatusFilter.map(s => s.replace(/_/g, ' ')).join(', ')}
            <button onClick={() => { setLeadStatusFilter([]); window.history.replaceState({}, '', window.location.pathname); }} className="ml-1 hover:text-indigo-900"><X className="w-3 h-3" /></button>
          </span>
        )}
      </div>

      {/* Bulk delete bar (super admin) */}
      {bulkMode && (
        <div className="flex flex-wrap items-center gap-3 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" data-testid="bulk-delete-bar">
          <Trash2 className="w-5 h-5 text-rose-600" />
          <span className="text-sm font-medium text-rose-800" data-testid="bulk-selected-count">
            {allMatching
              ? `All ${total} leads matching the current filters selected`
              : `${bulkSelected.length} lead${bulkSelected.length === 1 ? '' : 's'} selected`}
          </span>
          {!allMatching && total > 0 && (
            <button onClick={() => { setAllMatching(true); setBulkSelected([]); }}
              className="text-sm font-semibold text-rose-700 underline hover:text-rose-900"
              data-testid="bulk-select-all-matching">
              Select all {total} matching {activeFilterCount > 0 ? 'the filters' : 'leads'}
            </button>
          )}
          <div className="flex-1" />
          <button onClick={exitBulkMode} className="text-sm text-slate-500 hover:text-slate-700" data-testid="bulk-clear">Clear</button>
          <button
            onClick={handleBulkDelete}
            disabled={!bulkCount || bulkDeleting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4" /> {bulkDeleting ? 'Deleting…' : `Delete ${bulkCount || ''}`}
          </button>
        </div>
      )}

      {/* Merge Selection Bar */}
      {canMerge && selectedForMerge.length > 0 && (
        <div className="flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="merge-bar">
          <GitMerge className="w-5 h-5 text-amber-600" />
          <span className="text-sm text-amber-800 font-medium">{selectedForMerge.length}/2 leads selected for merge</span>
          <div className="flex-1" />
          <button onClick={() => setSelectedForMerge([])} className="text-sm text-slate-500 hover:text-slate-700">Clear</button>
          <button
            onClick={() => setShowMergeConfirm(true)}
            disabled={selectedForMerge.length !== 2}
            className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50"
            data-testid="merge-btn"
          >
            Merge Leads
          </button>
        </div>
      )}

      {/* Table */}
      <LeadTable
        leads={leads} loading={loading} total={total} page={page} totalPages={totalPages} goToPage={goToPage}
        user={user}
        onEdit={(lead) => { setEditingLead(lead); setShowModal(true); }}
        onDelete={handleDelete}
        onPayment={(lead) => { setSelectedLead(lead); setShowPaymentModal(true); }}
        onRefresh={fetchLeads}
        updateLeadLocal={updateLeadLocal}
        canMerge={canMerge && !bulkMode}
        selectedForMerge={selectedForMerge}
        toggleMergeSelect={toggleMergeSelect}
        bulkMode={bulkMode}
        bulkSelected={bulkSelected}
        toggleBulkSelect={toggleBulkSelect}
        toggleBulkSelectPage={toggleBulkSelectPage}
      />
      </>)}

      {/* Modals */}
      {showModal && (
        <LeadModal
          lead={editingLead}
          users={users}
          batches={batches}
          currentUser={user}
          onClose={() => { setShowModal(false); setEditingLead(null); }}
          onSave={() => { setShowModal(false); setEditingLead(null); fetchLeads(); }}
          onRefresh={fetchLeads}
        />
      )}

      {showPaymentModal && selectedLead && (
        <PaymentLinkModal
          lead={selectedLead}
          pricingConfigs={pricingConfigs}
          onClose={() => { setShowPaymentModal(false); setSelectedLead(null); }}
          onSuccess={() => { setShowPaymentModal(false); setSelectedLead(null); fetchLeads(); }}
        />
      )}

      {showQuickPayModal && (
        <QuickPaymentLinkModal
          pricingConfigs={pricingConfigs}
          onClose={() => setShowQuickPayModal(false)}
          onSuccess={() => fetchLeads()}
        />
      )}

      {/* Merge Confirmation Modal */}
      {showMergeConfirm && selectedForMerge.length === 2 && (() => {
        const lead1 = leads.find(l => l.lead_id === selectedForMerge[0]);
        const lead2 = leads.find(l => l.lead_id === selectedForMerge[1]);
        const newer = new Date(lead1?.created_at) > new Date(lead2?.created_at) ? lead1 : lead2;
        const older = newer === lead1 ? lead2 : lead1;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowMergeConfirm(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><GitMerge className="w-5 h-5 text-indigo-600" /> Merge Leads</h3>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-green-700 mb-1">KEEPING (Newer Lead)</p>
                  <p className="font-medium text-slate-900">{newer?.full_name}</p>
                  <p className="text-xs text-slate-500">{newer?.email} | {newer?.phone}</p>
                  <p className="text-xs text-slate-400">Created: {new Date(newer?.created_at).toLocaleDateString()}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-red-700 mb-1">MERGING INTO ABOVE (Older Lead — will be deleted)</p>
                  <p className="font-medium text-slate-900">{older?.full_name}</p>
                  <p className="text-xs text-slate-500">{older?.email} | {older?.phone}</p>
                  <p className="text-xs text-slate-400">Created: {new Date(older?.created_at).toLocaleDateString()}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
                  <p>Newer lead's data is kept. Blank fields filled from older lead.</p>
                  <p>More advanced statuses (WIP &gt; New, Scheduled &gt; Pending) are preserved.</p>
                  <p>All payments and bookings consolidated under the kept lead.</p>
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
                <button onClick={() => setShowMergeConfirm(false)} className="btn-secondary">Cancel</button>
                <button onClick={handleMerge} disabled={merging} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium" data-testid="confirm-merge-btn">
                  {merging ? 'Merging...' : 'Confirm Merge'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Save View Modal */}
      {showSaveViewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Save View</h3>
              <button onClick={() => setShowSaveViewModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">View Name</label>
                <input type="text" value={newViewName} onChange={(e) => setNewViewName(e.target.value)} className="input-base" placeholder="e.g., Scheduled Trials Today" maxLength={30} />
              </div>
              <p className="text-xs text-slate-500">{savedViews.length}/5 views used</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowSaveViewModal(false)} className="btn-secondary">Cancel</button>
                <button onClick={saveCurrentView} className="btn-primary">Save View</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
